import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle, Loader2, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Inserisci il tuo indirizzo email');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setEmailSent(true);
      toast.success('Controlla la tua casella email');
    } catch (error) {
      // Per sicurezza mostriamo sempre lo stesso messaggio
      setEmailSent(true);
      toast.success('Controlla la tua casella email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-slate-700/50">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <KeyRound className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl text-slate-800">
            Password dimenticata?
          </CardTitle>
          <CardDescription className="text-slate-500">
            {emailSent 
              ? 'Controlla la tua email per reimpostare la password'
              : 'Inserisci la tua email e ti invieremo un link per reimpostare la password'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {emailSent ? (
            <div className="space-y-6">
              {/* Stato: Email Inviata */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="font-semibold text-emerald-700 mb-2">Email Inviata!</h3>
                <p className="text-sm text-emerald-600">
                  Se l'indirizzo <strong>{email}</strong> è registrato nel sistema, 
                  riceverai un'email con le istruzioni per reimpostare la password.
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <p className="text-sm text-amber-700">
                  ⚠️ Il link è valido per <strong>1 ora</strong>. 
                  Controlla anche la cartella spam.
                </p>
              </div>
              
              <div className="text-center space-y-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEmailSent(false);
                    setEmail('');
                  }}
                  className="w-full"
                >
                  Prova con un'altra email
                </Button>
                
                <Link to="/login">
                  <Button variant="ghost" className="w-full text-slate-600">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Torna al Login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Indirizzo Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tuaemail@esempio.it"
                    className="pl-10"
                    autoComplete="email"
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full"
                disabled={isLoading}
                data-testid="forgot-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  'Invia Link di Reset'
                )}
              </Button>
              
              <Link to="/login">
                <Button variant="ghost" className="w-full text-slate-600">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Torna al Login
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
